import { Stack } from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import TextField, { type FilledTextFieldProps } from "@mui/material/TextField";
import React, {
  type ChangeEvent,
  type ComponentType,
  type CSSProperties,
  type FocusEventHandler,
  type KeyboardEventHandler,
  type ReactNode,
  type Ref,
  useState,
} from "react";
import styled from "styled-components";

import {
  type FilledTextFieldStyleParams as FilledFieldCustomProps,
  muiFilledTextFieldStyles,
} from "../features/shared/lib/muiFilledFieldStyles";
import { useDebouncedInputValueWithOnChange } from "../hooks/components";
import { searchInputEndAdornment } from "../utils/components";
import { cn, getInputDefaultStyles, isTrimmedEmpty } from "../utils/styling";
import { FormRowLabel } from "./FormRowLabel";
import { Icon } from "./Icon";

const DEFAULT_DEBOUNCE_TIME = 0;

const filledFieldPropKeys = new Set(["displayshrinkedlabel", "height", "labelcolor", "textcolor"]);

const shouldForwardFilledFieldProp = (prop: PropertyKey): boolean => !filledFieldPropKeys.has(prop as string);

type CommonInputProps = Record<string, any> & {
  className?: string;
  debounceTime?: number;
  disabled?: boolean;
  endAdornment?: ReactNode;
  height?: string;
  id?: string;
  inputRef?: Ref<HTMLInputElement>;
  label?: ReactNode;
  multiline?: boolean;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  type?: string;
  value?: any;
  width?: number | string;
};

type FilledFieldStyleParams = FilledFieldCustomProps & FilledTextFieldProps;

export const Input = ({
  className,
  debounceTime = DEFAULT_DEBOUNCE_TIME,
  disabled,
  endAdornment,
  height,
  id,
  inputRef,
  label,
  multiline,
  onChange,
  placeholder,
  type,
  value,
  width,
  ...props
}: CommonInputProps) => {
  const [isEmpty, setIsEmpty] = useState(isTrimmedEmpty(value));
  const [HasError] = useState(false);

  const { handleChange, inputValue } = useDebouncedInputValueWithOnChange({ debounceTime, onChange, value });

  const { labelColor: inputLabelColor, textColor: inputTextColor } = getInputDefaultStyles({
    disabled,
    HasError,
    isEmpty,
  });

  return (
    <span
      className={className}
      style={{
        display: "block",
        width: width,
      }}
      {...props}
    >
      <CustomTextField
        autoComplete="on"
        disabled={disabled}
        displayshrinkedlabel={type === "search" ? "false" : "true"}
        error={HasError}
        fullWidth
        height={height}
        id={id}
        InputLabelProps={{
          classes: { focused: "!font-ds-body-regular-lg", shrink: "!font-ds-body-regular-lg" },
          className: "font-ds-body-regular-md",
          style: {
            color: inputLabelColor,
            textTransform: "capitalize",
            transform: type === "search" ? "translate(1.2rem, 1.4rem)" : "",
          },
        }}
        InputProps={{ className: "font-ds-body-regular-lg", ...(endAdornment ? { endAdornment } : {}) }}
        inputProps={{ className: "font-ds-body-regular-lg" }}
        inputRef={inputRef}
        label={label}
        labelcolor={inputLabelColor}
        multiline={multiline}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          if (inputRef && "current" in inputRef && inputRef.current) {
            setIsEmpty(isTrimmedEmpty(inputRef.current.value));
          } else {
            setIsEmpty(isTrimmedEmpty(e.target.value));
          }
          handleChange(e);
        }}
        placeholder={placeholder}
        textcolor={inputTextColor}
        type={`${type === "search" ? "" : type}`}
        value={inputValue}
        variant="filled"
      />
    </span>
  );
};

type PasswordInputProps = {
  className?: string;
  debounceTime?: number;
  height?: string;
  id?: string;
  inputRef?: Ref<HTMLInputElement>;
  label?: ReactNode;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  width?: number | string;
};

export const PasswordInput = ({
  className,
  debounceTime,
  height,
  id,
  inputRef,
  label,
  onChange,
  placeholder,
  width,
}: PasswordInputProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const handleClickShowPassword = () => setShowPassword((show) => !show);

  return (
    <Input
      className={className}
      debounceTime={debounceTime}
      endAdornment={
        <InputAdornment position="end">
          {showPassword ? (
            <Icon
              className="cursor-pointer"
              iconName="EyeClose"
              onClick={handleClickShowPassword}
              size="sm"
              svgClassName="w-4 h-4"
            />
          ) : (
            <Icon
              className="cursor-pointer"
              iconName="EyeOpen"
              onClick={handleClickShowPassword}
              size="sm"
              svgClassName="w-4 h-4"
            />
          )}
        </InputAdornment>
      }
      height={height}
      id={id}
      inputRef={inputRef}
      label={label}
      onChange={onChange}
      placeholder={placeholder}
      type={showPassword ? "text" : "password"}
      width={width}
    />
  );
};

type SearchInputProps = {
  className?: string;
  debounceTime?: number;
  disabled?: boolean;
  height?: string;
  id?: string;
  label?: ReactNode;
  onBlur?: () => void;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  style?: CSSProperties;
  type?: string;
  value?: any;
  width?: number | string;
};

export const SearchInput = ({
  className,
  debounceTime = DEFAULT_DEBOUNCE_TIME,
  disabled,
  height,
  id,
  label,
  onBlur,
  onChange,
  placeholder,
  style,
  type,
  value,
  width,
}: SearchInputProps) => {
  const [isEmpty, setIsEmpty] = useState(isTrimmedEmpty(value));
  const [HasError] = useState(false);

  const { handleChange, inputValue } = useDebouncedInputValueWithOnChange({ debounceTime, onChange, value });

  const { labelColor: inputLabelColor, textColor: inputTextColor } = getInputDefaultStyles({
    disabled,
    HasError,
    isEmpty,
  });

  return (
    <span
      className={className}
      style={{
        display: "block",
        width: width,
        ...style,
      }}
    >
      <CustomTextField
        disabled={disabled}
        displayshrinkedlabel={type === "search" ? "false" : "true"}
        error={HasError}
        fullWidth
        height={height}
        id={id}
        InputLabelProps={{
          classes: { focused: "!font-ds-body-regular-lg" },
          className: "font-ds-body-regular-md ",
          style: {
            color: inputLabelColor,
            textTransform: "capitalize",
            transform: type === "search" ? "translate(1.2rem, 1.4rem)" : "",
          },
        }}
        InputProps={{
          className: "font-ds-body-regular-lg",
          ...(type === "search" ? searchInputEndAdornment : {}),
        }}
        inputProps={{ className: "font-ds-body-regular-lg" }}
        label={label}
        labelcolor={inputLabelColor}
        onBlur={() => {
          if (onBlur) onBlur();
        }}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          setIsEmpty(isTrimmedEmpty(e.target.value));
          handleChange?.(e);
        }}
        placeholder={placeholder}
        textcolor={inputTextColor}
        type={`${type === "search" ? "" : type}`}
        value={inputValue}
        variant="filled"
      />
    </span>
  );
};

type TextFieldInputProps = LabelLessInputProps & {
  label?: ReactNode;
  required?: boolean;
  rootClassName?: string;
};

export const TextFieldInput = ({ label, required, rootClassName, ...props }: TextFieldInputProps) => {
  return (
    <Stack className={cn("flex-col items-start gap-2", rootClassName)}>
      <FormRowLabel label={label} required={required} />
      <LabelLessInput {...props} />
    </Stack>
  );
};

export type LabelLessInputProps = Record<string, any> & {
  autoFocus?: boolean;
  className?: string;
  debounceTime?: number;
  disabled?: boolean;
  height?: string;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  placeholder?: string;
  style?: CSSProperties;
  type?: string;
  value?: any;
  width?: number | string;
};

export const LabelLessInput = ({
  autoFocus,
  className,
  debounceTime = DEFAULT_DEBOUNCE_TIME,
  disabled,
  height,
  onBlur,
  onChange,
  onKeyDown,
  placeholder,
  style,
  type,
  value,
  width,
  ...props
}: LabelLessInputProps) => {
  const [isEmpty, setIsEmpty] = useState(isTrimmedEmpty(value));
  const [HasError] = useState(false);

  const { textColor } = getInputDefaultStyles({
    disabled,
    HasError,
    isEmpty,
  });

  const { handleChange, inputValue } = useDebouncedInputValueWithOnChange({ debounceTime, onChange, value });

  return (
    <span
      className={className}
      style={{
        display: "block",
        width: width,
        ...style,
      }}
      {...props}
    >
      <LabelLessTextField
        autoFocus={autoFocus}
        disabled={disabled}
        displayshrinkedlabel="false"
        error={HasError}
        fullWidth
        height={height}
        InputProps={{ className: "font-ds-body-regular-lg" }}
        onBlur={onBlur}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          setIsEmpty(isTrimmedEmpty(e.target.value));
          handleChange?.(e);
        }}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        textcolor={textColor}
        type={type}
        value={inputValue}
        variant="filled"
      />
    </span>
  );
};

type ColorInputProps = {
  className?: string;
  color: string;
  debounceTime?: number;
  height?: string;
  inputRef?: Ref<HTMLInputElement>;
  onBlur?: () => void;
  setColor: (color: string) => void;
  width?: string;
};

export const ColorInput = ({
  className,
  color,
  debounceTime = DEFAULT_DEBOUNCE_TIME,
  height,
  inputRef,
  onBlur,
  setColor,
  width,
}: ColorInputProps) => {
  const { handleChange, inputValue } = useDebouncedInputValueWithOnChange({
    debounceTime,
    onChange: (e: ChangeEvent<HTMLInputElement>) => setColor(e.target.value),
    value: color,
  });

  return (
    <input
      className={cn("block cursor-pointer rounded-ds-0-25x", className)}
      onBlur={onBlur}
      onChange={(e) => handleChange(e)}
      ref={inputRef}
      style={{ height, width }}
      type="color"
      value={inputValue}
    />
  );
};

const CustomTextField = styled(TextField as any).withConfig({
  shouldForwardProp: shouldForwardFilledFieldProp,
})(
  (p: FilledFieldStyleParams) => muiFilledTextFieldStyles(p, { withFloatingLabel: true }) as any
) as unknown as ComponentType<FilledFieldStyleParams>;

export const LabelLessTextField = styled(TextField as any).withConfig({
  shouldForwardProp: shouldForwardFilledFieldProp,
})(
  (p: FilledFieldStyleParams) => muiFilledTextFieldStyles(p, { withFloatingLabel: false }) as any
) as unknown as ComponentType<FilledFieldStyleParams>;
